import { LockOutlined } from '@ant-design/icons'
import { Form, Input, message, Spin } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { accountApi } from 'common/api/entitiesApi'
import {AnchorsForm} from 'common/components/AnchorsForm'
import { ButtonsPanel } from 'common/components/ButtonsPanel.tsx'
import { TimeZoneSelector } from 'common/components/dictionary/TimeZoneSelector.tsx'
import { FormCard } from 'common/components/FormCard.tsx'
import { useUserContext } from 'common/context/UserContext'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CommonRoutes } from 'common/context/commonRoutes.ts'

const Profile = () => {
  const [form] = Form.useForm()
  const navigateTo = useNavigate()
  const { user } = useUserContext()
  const { id } = useParams<{ id?: string }>()
  const resolvedId = id ? parseInt(id) : user?.id
  const [loading, setLoading] = useState(false)
  const [entity, setEntity] = useState<any>({})
  const [messageApi, contextHolder] = message.useMessage()

  const loadData = () => {
    if (!resolvedId) return
    setLoading(true)
    accountApi
      .account(resolvedId)
      .then((res) => {
        if (res?.data) {
          setEntity(res.data)
          form.setFieldsValue(res.data)
        }
      })
      .catch((e) => showError(e))
      .finally(() => setLoading(false))
  }

  const showError = (e: any) => {
    const content = e?.error || e?.message || 'Unexpected error'
    messageApi.open({
      type: 'error',
      content,
      duration: 5,
      style: { marginTop: 80 },
    })
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (!resolvedId) return
      setLoading(true)

      // Prepare payload: merge existing entity, override with form values
      const payload: any = {
        ...entity,
        ...values,
      }

      accountApi
        .editAccount(payload, resolvedId)
        .then(() => {
          messageApi.success('Profile saved')
          navigateTo(CommonRoutes.CAMPAIGN)
        })
        .catch((e) => showError(e))
        .finally(() => setLoading(false))
    })
  }

  useEffect(() => {
    loadData()
  }, [resolvedId])

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <AnchorsForm form={form} onFinish={handleSubmit}>
        <FormCard title={'Profile'}>
          <FormItem label="Name" name="name">
            <Input readOnly />
          </FormItem>

          <FormItem label="Email" name="email">
            <Input readOnly />
          </FormItem>

          <FormItem
            label="Time Zone"
            name="timezoneId"
            rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]}
          >
            <TimeZoneSelector />
          </FormItem>

          <FormItem
            label="Current Password"
            name="currentPassword"
            dependencies={['password']}
            tooltip="Required to change your password"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue('password') && !value) return Promise.reject('Enter your current password')
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              autoComplete="current-password"
              placeholder="Current Password"
            />
          </FormItem>

          <FormItem label="Password" name="password" tooltip="Leave empty to keep current password">
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              autoComplete="new-password"
              placeholder="New Password"
            />
          </FormItem>

          <FormItem
            label="Confirm"
            name="passwordConfirm"
            dependencies={['password']}
            hasFeedback
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const pwd = getFieldValue('password')
                  if (!pwd && !value) return Promise.resolve()
                  if (pwd === value) return Promise.resolve()
                  return Promise.reject('Passwords do not match')
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              autoComplete="new-password"
              placeholder="Confirm Password"
            />
          </FormItem>
        </FormCard>

        <ButtonsPanel onCancel={() => navigateTo(CommonRoutes.CAMPAIGN)} />
      </AnchorsForm>
    </Spin>
  )
}

export default Profile
